import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, payableInr } from "@/lib/api";
import { EiraPointsCountdown } from "@/components/EiraPointsCountdown";
import { toast } from "sonner";
import { ChevronDown, Minus, Plus, Sparkles, Ticket } from "lucide-react";
import { FoodSymbol } from "@/components/FoodSymbol";
import { formatIndianPhone, PhoneNumberInput } from "@/components/PhoneNumberInput";
import { cartRequestItem, cartStockLimit } from "@/lib/cartInventory";
import RecentlyViewedProducts from "@/components/RecentlyViewedProducts";
import { trackMetaPurchase } from "@/lib/analytics";

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
const PAYMENT_CANCELLED = "PAYMENT_CANCELLED";

const loadRazorpayCheckout = () => new Promise((resolve, reject) => {
  if (window.Razorpay) {
    resolve(window.Razorpay);
    return;
  }
  const existingScript = document.querySelector('script[data-eira-razorpay="true"]');
  const script = existingScript || document.createElement("script");
  const onLoad = () => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    reject(new Error("Secure payment could not be loaded"));
  };
  const onError = () => reject(new Error("Secure payment could not be loaded"));
  script.addEventListener("load", onLoad, { once: true });
  script.addEventListener("error", onError, { once: true });
  if (!existingScript) {
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.dataset.eiraRazorpay = "true";
    document.body.appendChild(script);
  }
});

const Field = ({ label, ...props }) => (
  <label className="block min-w-0 text-sm">
    <span className="break-words font-medium text-[#1E2A24]">{label}</span>
    <input {...props} className="mt-1 min-w-0 w-full bg-white border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]" />
  </label>
);

const couponBenefitLabel = (coupon, benefit) => {
  if (benefit?.kind === "bogo") return "Buy 1 Get 1 Free";
  if (benefit?.kind === "free_shipping") return "Free delivery";
  if (benefit?.free_item) return `Free gift: ${benefit.free_item.name}`;
  return coupon?.description || "Offer applied";
};

const availableOfferLabel = (coupon) => {
  if (coupon.benefit?.kind === "bogo") return "Buy 1 Get 1 Free";
  if (coupon.benefit?.free_shipping) return "Free delivery";
  if (coupon.benefit?.free_item) return `Free ${coupon.benefit.free_item.name}`;
  if (coupon.discount > 0) return `Save ${payableInr(coupon.discount)}`;
  return coupon.description || "Eligible offer";
};

const automaticOfferValue = (coupon) => {
  if (coupon.benefit?.kind === "bogo") return Number(coupon.discount || 0);
  if (coupon.benefit?.free_item) {
    return Number(coupon.benefit.free_item.original_price || 0);
  }
  return -1;
};

const Checkout = () => {
  const { items, subtotal, applyServerCart, setQty } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [addr, setAddr] = useState({ full_name: user?.name || "", mobile: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India" });
  const [coupon, setCoupon] = useState(params.get("coupon") || "");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponBenefit, setCouponBenefit] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [offersOpen, setOffersOpen] = useState(false);
  const [autoAppliedCouponCode, setAutoAppliedCouponCode] = useState("");
  const [isCouponInputManual, setIsCouponInputManual] = useState(false);
  const announcedAutoOffer = useRef("");
  const [payMethod, setPayMethod] = useState("razorpay");
  const [paymentMethods, setPaymentMethods] = useState({
    razorpay_enabled: true,
    cod_enabled: true,
  });
  const [paymentMethodsLoaded, setPaymentMethodsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [points, setPoints] = useState({ available_points: 0, pending_points: 0 });
  const [pointsToRedeem, setPointsToRedeem] = useState("");
  const [pointsEnabled, setPointsEnabled] = useState(false);
  const [useAllPoints, setUseAllPoints] = useState(false);
  const [pointsInputError, setPointsInputError] = useState("");
  const [gstDetails, setGstDetails] = useState({
    company_name: "",
    gstin: "",
    company_address: "",
  });
  const [gstLoaded, setGstLoaded] = useState(false);
  const [useGstInvoice, setUseGstInvoice] = useState(false);
  const hasGiftBox = items.some((item) => item.product_type === "gift_box");
  const totalUnits = items.reduce((sum, item) => sum + item.quantity, 0)
    + (couponBenefit?.free_item?.quantity || 0);
  const codItemsEligible = !hasGiftBox && totalUnits <= 5;

  const getCheckoutAttemptId = (cartItems) => {
    const storageKey = `eira_checkout_attempt:${user.id}`;
    const cartFingerprint = JSON.stringify({ cartItems, payMethod, coupon: appliedCoupon?.code || null, points: pointsDiscount, address: addr, gst: useGstInvoice ? gstDetails : null });
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || "null");
      if (saved?.attemptId && saved.cartFingerprint === cartFingerprint) {
        return { storageKey, attemptId: saved.attemptId };
      }
      const attemptId = window.crypto?.randomUUID?.()
        || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(storageKey, JSON.stringify({ attemptId, cartFingerprint }));
      return { storageKey, attemptId };
    } catch {
      return {
        storageKey,
        attemptId: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
      };
    }
  };

  useEffect(() => {
    if (!user) return;
    api.get("/me/addresses").then((response) => {
      const savedAddress = response.data.at(-1);
      if (savedAddress) {
        const { id, label, ...address } = savedAddress;
        setAddr((currentAddress) => ({ ...currentAddress, ...address }));
      }
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get("/me/gst-details")
      .then((response) => setGstDetails(response.data))
      .catch(() => {})
      .finally(() => setGstLoaded(true));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get("/me/eira-points").then((response) => setPoints(response.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    api.get("/payment-methods")
      .then((response) => setPaymentMethods(response.data))
      .catch(() => {})
      .finally(() => setPaymentMethodsLoaded(true));
  }, []);

  useEffect(() => {
    if (!paymentMethodsLoaded) return;
    const enabledMethods = [
      paymentMethods.razorpay_enabled ? "razorpay" : null,
      paymentMethods.cod_enabled && codItemsEligible ? "cod" : null,
    ].filter(Boolean);
    if (enabledMethods.length && !enabledMethods.includes(payMethod)) {
      setPayMethod(enabledMethods[0]);
    }
  }, [payMethod, paymentMethods, paymentMethodsLoaded, codItemsEligible]);

  const couponCart = useMemo(() => items.map(cartRequestItem), [items]);
  const appliedCouponCode = appliedCoupon?.code;

  useEffect(() => {
    if (!user || !items.length) return;
    api.post("/coupons/available", { subtotal, cart: couponCart })
      .then(({ data }) => {
        setAvailableCoupons(data);
        const automaticOffers = data
          .filter((item) => ["bogo", "free_item"].includes(item.benefit?.kind))
          .sort((left, right) => automaticOfferValue(right) - automaticOfferValue(left));
        const preferredOffer = automaticOffers[0];
        const canReplaceAppliedCoupon = !appliedCoupon || ["bogo", "free_item"].includes(
          appliedCoupon.kind,
        );
        if (
          preferredOffer
          && canReplaceAppliedCoupon
          && !isCouponInputManual
          && appliedCoupon?.code !== preferredOffer.code
        ) {
          setCoupon(preferredOffer.code);
          setAppliedCoupon(preferredOffer);
          setDiscount(preferredOffer.discount);
          setCouponBenefit(preferredOffer.benefit || null);
          setAutoAppliedCouponCode(preferredOffer.code);
          setIsCouponInputManual(false);
          if (announcedAutoOffer.current !== preferredOffer.code) {
            announcedAutoOffer.current = preferredOffer.code;
            toast.success(`${preferredOffer.code} applied automatically`);
          }
          return;
        }
        const welcomeBack = data.find((item) => item.usage_mode === "welcomeback");
        if (welcomeBack && !appliedCoupon && !isCouponInputManual) {
          setCoupon(welcomeBack.code);
          setAppliedCoupon(welcomeBack);
          setDiscount(welcomeBack.discount);
          setCouponBenefit(welcomeBack.benefit || null);
        }
      })
      .catch(() => setAvailableCoupons([]));
  }, [appliedCoupon, couponCart, isCouponInputManual, items.length, subtotal, user]);

  useEffect(() => {
    if (!appliedCouponCode || !user) return;
    api.post("/coupons/validate", {
      code: appliedCouponCode,
      subtotal,
      cart: couponCart,
    })
      .then(({ data }) => {
        setDiscount(data.discount);
        setAppliedCoupon(data.coupon);
        setCouponBenefit(data.benefit || null);
      })
      .catch(() => {
        setAppliedCoupon(null);
        setDiscount(0);
        setCouponBenefit(null);
        setAutoAppliedCouponCode("");
      });
  }, [appliedCouponCode, couponCart, subtotal, user]);

  // Auto-apply coupon from URL (one-tap recovery link)
  useEffect(() => {
    const c = params.get("coupon");
    if (c && subtotal > 0 && !appliedCoupon) {
      api.post("/coupons/validate", { code: c, subtotal, cart: couponCart })
        .then(({ data }) => {
          setAppliedCoupon(data.coupon);
          setDiscount(data.discount);
          setCouponBenefit(data.benefit || null);
          setAutoAppliedCouponCode("");
          toast.success(`Coupon ${c} applied automatically`);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  useEffect(() => {
    const couponNeedsPointsThreshold = ["bogo", "free_item"].includes(couponBenefit?.kind);
    const amountAfterCoupon = Math.max(subtotal - discount, 0);
    const amountBeforePoints = amountAfterCoupon + (
      couponBenefit?.free_shipping || amountAfterCoupon >= 499 ? 0 : 49
    );
    if (couponNeedsPointsThreshold && amountBeforePoints <= 999) {
      setPointsToRedeem("");
      setPointsEnabled(false);
      setUseAllPoints(false);
      setPointsInputError("");
    }
  }, [couponBenefit?.free_shipping, couponBenefit?.kind, discount, subtotal]);

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-md text-center">
          <p className="mb-4">Please login to checkout.</p>
          <Link to="/login" className="bg-[#2D5A47] px-5 py-2.5 text-white rounded-lg">
            Login
          </Link>
        </div>
        <RecentlyViewedProducts className="mt-16" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-md text-center">
          Your cart is empty. {" "}
          <Link to="/shop" className="font-medium text-[#C86D42]">
            Continue shopping
          </Link>
        </div>
        <RecentlyViewedProducts className="mt-16" />
      </div>
    );
  }

  const couponFieldMatchesApplied = Boolean(appliedCoupon) && (
    appliedCoupon.code?.toUpperCase() === coupon.trim().toUpperCase()
  );
  const productAmountAfterCoupon = Math.max(subtotal - discount, 0);
  const currentTier = points.loyalty_tier?.current_tier;
  const tierDiscountPercent = Number(currentTier?.discount_percent || 0);
  const tierRedemptionPercent = Number(
    currentTier?.redemption_percent ?? points.config?.maximum_discount_percent ?? 100,
  );
  const tierDiscount = Number((productAmountAfterCoupon * tierDiscountPercent / 100).toFixed(2));
  const productAmountAfterTier = Math.max(productAmountAfterCoupon - tierDiscount, 0);
  const normalizedPointsToRedeem = pointsToRedeem !== "" && Number.isFinite(Number(pointsToRedeem))
    ? Math.max(Number(pointsToRedeem), 0)
    : 0;
  const requestedPoints = pointsEnabled
    ? Math.min(normalizedPointsToRedeem, points.available_points || 0)
    : 0;
  const pointsRestrictedCoupon = ["bogo", "free_item"].includes(couponBenefit?.kind);
  const pointsOnProductsBeforeShipping = Math.min(requestedPoints, productAmountAfterTier);
  const productAmountAfterAllDiscounts = Math.max(
    productAmountAfterTier - pointsOnProductsBeforeShipping,
    0,
  );
  const shippingBeforePoints = couponBenefit?.free_shipping || productAmountAfterAllDiscounts >= 499
    ? 0
    : 49;
  const amountBeforePoints = productAmountAfterTier + (
    couponBenefit?.free_shipping || productAmountAfterTier >= 499 ? 0 : 49
  );
  const pointsBlockedByCouponThreshold = pointsRestrictedCoupon && amountBeforePoints <= 999;
  const maxPointsToRedeem = Math.min(
    pointsBlockedByCouponThreshold ? 0 : points.available_points || 0,
    (productAmountAfterTier + shippingBeforePoints) * tierRedemptionPercent / 100,
  );
  const pointsDiscount = pointsEnabled
    ? Math.min(normalizedPointsToRedeem, maxPointsToRedeem)
    : 0;
  const pointsOnProducts = Math.min(pointsDiscount, productAmountAfterTier);
  const pointsOnShipping = Number((pointsDiscount - pointsOnProducts).toFixed(2));
  const inclusiveProducts = Math.max(productAmountAfterTier - pointsOnProducts, 0);
  const shipping = Math.max(shippingBeforePoints - pointsOnShipping, 0);
  const baseAmount = Number((inclusiveProducts / 1.18).toFixed(2));
  const gst = Number((inclusiveProducts - baseAmount).toFixed(2));
  const total = Number((inclusiveProducts + shipping).toFixed(2));
  const codAvailable = total >= 499 && codItemsEligible;
  const codReason = hasGiftBox ? "Gift boxes and hampers require online payment." : totalUnits > 5 ? "Cash on Delivery allows up to 5 total units. Choose online payment for larger orders." : "Cash on Delivery is available on orders of ₹499 and above.";
  const selectedPaymentAvailable = payMethod === "razorpay" ? paymentMethods.razorpay_enabled : paymentMethods.cod_enabled && codAvailable;
  const pointsToggleOn = pointsEnabled && pointsDiscount > 0;
  const usingAllAvailablePoints = pointsToggleOn && useAllPoints;

  const applyCouponCode = async (code, closeOffers = false) => {
    try {
      const { data } = await api.post("/coupons/validate", {
        code,
        subtotal,
        cart: couponCart,
      });
      setCoupon(data.coupon.code);
      setAppliedCoupon(data.coupon);
      setDiscount(data.discount);
      setCouponBenefit(data.benefit || null);
      setAutoAppliedCouponCode("");
      setIsCouponInputManual(true);
      if (closeOffers) setOffersOpen(false);
      toast.success("Coupon applied");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const applyCoupon = () => applyCouponCode(coupon);

  const removeCoupon = (closeOffers = false) => {
    setCoupon("");
    setAppliedCoupon(null);
    setDiscount(0);
    setCouponBenefit(null);
    setAutoAppliedCouponCode("");
    setIsCouponInputManual(true);
    if (closeOffers) setOffersOpen(false);
    toast.success("Coupon removed");
  };

  const toggleAllPoints = () => {
    if (pointsToggleOn) {
      setPointsEnabled(false);
      setUseAllPoints(false);
      return;
    }
    if (normalizedPointsToRedeem > 0) {
      setPointsEnabled(true);
      setUseAllPoints(false);
      return;
    }
    setPointsInputError("");
    setPointsEnabled(true);
    setUseAllPoints(true);
    setPointsToRedeem(maxPointsToRedeem);
  };

  const setCustomPoints = (value) => {
    setUseAllPoints(false);
    if (value === "") {
      setPointsToRedeem("");
      setPointsEnabled(false);
      setPointsInputError("");
      return;
    }
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
      setPointsToRedeem("");
      setPointsEnabled(false);
      setPointsInputError("Enter a valid Eira Points amount.");
      return;
    }
    if (parsedValue > maxPointsToRedeem) {
      setPointsToRedeem(maxPointsToRedeem);
      setPointsInputError(`You can apply up to ${payableInr(maxPointsToRedeem)} Eira Points.`);
      return;
    }
    setPointsToRedeem(value);
    setPointsInputError("");
  };

  const openRazorpayCheckout = async (payment, orderId) => {
    if (!payment?.key_id || !payment?.razorpay_order_id || !payment?.amount) {
      throw new Error("Online payment could not be started. Please try again.");
    }
    const paymentAmount = Number(payment.amount);
    if (!Number.isSafeInteger(paymentAmount) || paymentAmount <= 0) {
      throw new Error("Online payment amount could not be verified. Please try again.");
    }
    const Razorpay = await loadRazorpayCheckout();
    return new Promise((resolve, reject) => {
      let settled = false;
      let verificationStarted = false;
      const cancelPendingPayment = async () => {
        try {
          const response = await api.post(`/orders/${orderId}/payment-cancelled`);
          if (response.data.eira_points) {
            setPoints(response.data.eira_points);
          }
        } catch {
          // The cart stays available even if a cancellation cleanup request times out.
        }
      };
      const rejectAsCancelled = async () => {
        if (settled || verificationStarted) {
          return;
        }
        settled = true;
        await cancelPendingPayment();
        const error = new Error("Payment cancelled");
        error.code = PAYMENT_CANCELLED;
        reject(error);
      };
      const checkout = new Razorpay({
        key: payment.key_id,
        amount: paymentAmount,
        currency: payment.currency || "INR",
        name: "Eira Products",
        description: `Order ${orderId.slice(0, 8).toUpperCase()}`,
        order_id: payment.razorpay_order_id,
        prefill: {
          name: addr.full_name,
          email: user.email,
          contact: addr.mobile.replace(/\D/g, "").slice(-10),
        },
        theme: { color: "#2D5A47" },
        modal: { ondismiss: rejectAsCancelled },
        handler: async (response) => {
          verificationStarted = true;
          try {
            const verification = await api.post("/orders/verify", {
              order_id: orderId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            settled = true;
            resolve(verification.data);
          } catch (error) {
            settled = true;
            reject(error);
          }
        },
      });
      checkout.on("payment.failed", () => {
        if (!settled && !verificationStarted) {
          toast.error("Payment failed. Retry securely in the payment window or close it to return to your bag.");
        }
      });
      checkout.open();
    });
  };

  const placeOrder = async () => {
    if (!addr.full_name || !addr.mobile || !addr.line1 || !addr.city || !addr.state || !addr.pincode) {
      toast.error("Please fill address");
      return;
    }
    if (!paymentMethods.razorpay_enabled && !paymentMethods.cod_enabled) {
      toast.error("No payment method is available right now.");
      return;
    }
    if (!selectedPaymentAvailable) {
      toast.error(payMethod === "cod" ? codReason : "Online payment is currently unavailable.");
      return;
    }
    let checkoutStorageKey = "";
    setLoading(true);
    try {
      const cartItems = items.map(cartRequestItem);
      const { storageKey, attemptId } = getCheckoutAttemptId(cartItems);
      checkoutStorageKey = storageKey;
      const { data } = await api.post("/orders/checkout", {
        address: { ...addr, mobile: formatIndianPhone(addr.mobile) },
        coupon_code: appliedCoupon?.code || null,
        payment_method: payMethod,
        cart: cartItems,
        checkout_attempt_id: attemptId,
        points_to_redeem: pointsDiscount,
        use_gst_invoice: useGstInvoice,
        gst_details: useGstInvoice && gstLoaded ? gstDetails : undefined,
      });
      if (data.eira_points) {
        setPoints(data.eira_points);
      }
      const orderId = data.order.id;
      let authoritativeCart = data.cart;
      if (payMethod === "razorpay") {
        const checkoutTotalPaise = Math.round(Number(data.order?.totals?.total || 0) * 100);
        if (checkoutTotalPaise !== Number(data.razorpay?.amount)) {
          throw new Error("Checkout total changed. Please review your order and try again.");
        }
        const verification = await openRazorpayCheckout(data.razorpay, orderId);
        authoritativeCart = verification.cart;
        if (verification.eira_points) {
          setPoints(verification.eira_points);
        }
      }
      if (authoritativeCart) {
        applyServerCart(authoritativeCart);
      }
      trackMetaPurchase(data.order);
      sessionStorage.removeItem(storageKey);
      toast.success("Order placed successfully!");
      nav(`/order/${orderId}`);
    } catch (e) {
      if (e?.code === PAYMENT_CANCELLED) {
        sessionStorage.removeItem(checkoutStorageKey);
        toast.message("Payment cancelled. Your bag is unchanged.");
      } else {
        toast.error(formatError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8" data-testid="checkout-page">
      <h1 className="font-serif text-4xl font-semibold mb-8">Checkout</h1>
      <div className="grid min-w-0 grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <div className="min-w-0 bg-white border border-[#E2DCD2] rounded-xl p-6">
            <h2 className="font-serif text-2xl mb-4">Shipping Address</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" data-testid="ck-name" value={addr.full_name} onChange={(e) => setAddr({ ...addr, full_name: e.target.value })} />
              <label className="block min-w-0 text-sm">
                <span className="break-words font-medium text-[#1E2A24]">Mobile</span>
                <PhoneNumberInput
                  value={addr.mobile}
                  onChange={(value) => setAddr({ ...addr, mobile: value })}
                  required
                  testId="ck-mobile"
                />
              </label>
              <div className="sm:col-span-2"><Field label="Address" data-testid="ck-line1" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} /></div>
              <div className="sm:col-span-2"><Field label="Apartment/Landmark (optional)" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} /></div>
              <Field label="City" data-testid="ck-city" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
              <Field label="State" data-testid="ck-state" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} />
              <Field label="Pincode" data-testid="ck-pin" value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} />
              <Field
                label="Country"
                value={addr.country}
                onChange={(e) => setAddr({ ...addr, country: e.target.value })}
                data-testid="ck-country"
              />
            </div>
          </div>

          <div className="min-w-0 bg-white border border-[#E2DCD2] rounded-xl p-6" data-testid="checkout-gst-details">
            <h2 className="font-serif text-2xl">GST Details</h2>
            <p className="mt-1 text-sm text-[#64748B]">
              Optional. GST details are used only when selected for this order.
            </p>
            <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm font-semibold">
              <input
                type="checkbox"
                checked={useGstInvoice}
                onChange={(event) => setUseGstInvoice(event.target.checked)}
                className="h-4 w-4 accent-[#2D5A47]"
                data-testid="use-gst-invoice-checkbox"
              />
              Use GST Invoice for this order
            </label>
            {useGstInvoice && (
              <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="checkout-gst-form">
                <p className="sm:col-span-2 text-xs text-[#64748B]">
                  {gstDetails.company_name || gstDetails.gstin
                    ? "Your saved GST details have been filled in below."
                    : "Add GST details for this invoice. You can also save them to your profile."}
                </p>
                <Field
                  label="Company Name"
                  value={gstDetails.company_name}
                  onChange={(event) => setGstDetails({ ...gstDetails, company_name: event.target.value })}
                  data-testid="checkout-gst-company-name"
                />
                <Field
                  label="GSTIN"
                  value={gstDetails.gstin}
                  onChange={(event) => setGstDetails({ ...gstDetails, gstin: event.target.value.toUpperCase() })}
                  data-testid="checkout-gst-gstin"
                />
                <div className="sm:col-span-2">
                  <label className="block min-w-0 text-sm">
                    <span className="break-words font-medium text-[#1E2A24]">Company Address</span>
                    <textarea
                      rows={3}
                      value={gstDetails.company_address}
                      onChange={(event) => setGstDetails({ ...gstDetails, company_address: event.target.value })}
                      className="mt-1 min-w-0 w-full bg-white border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]"
                      data-testid="checkout-gst-company-address"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white border border-[#E2DCD2] rounded-xl p-6">
            <h2 className="font-serif text-2xl mb-4">Payment Method</h2>
            <p className="mb-4 text-sm text-[#64748B]" data-testid="checkout-quantity-policy">Online: any quantity up to available stock. COD: up to 5 total units. Gift boxes: online only.</p>
            <div className="space-y-3">
              {[
                paymentMethods.razorpay_enabled && {
                  v: "razorpay",
                  t: "Razorpay (UPI, Card, Net Banking)",
                  s: "Secure online payment",
                },
                paymentMethods.cod_enabled && {
                  v: "cod",
                  t: "Cash on Delivery",
                  s: codAvailable ? "Up to 5 total units · Pay when your order arrives" : codReason,
                },
              ].filter(Boolean).map((m) => {
                const isUnavailableCod = m.v === "cod" && !codAvailable;
                return <label key={m.v} onClick={() => isUnavailableCod && toast.error(codReason)} className={`block border rounded-lg p-4 ${isUnavailableCod ? "cursor-not-allowed opacity-50 bg-[#F4F1EA]" : "cursor-pointer"} ${payMethod === m.v && !isUnavailableCod ? "border-[#2D5A47] bg-[#EBF2EE]" : "border-[#E2DCD2]"}`}>
                  <input type="radio" name="pay" value={m.v} checked={payMethod === m.v} disabled={isUnavailableCod} onChange={() => setPayMethod(m.v)} className="mr-2" data-testid={`pay-${m.v}`} />
                  <span className="font-medium">{m.t}</span>
                  <div className="text-xs text-[#64748B] ml-6">{m.s}</div>
                </label>;
              })}
              {paymentMethodsLoaded
                && !paymentMethods.razorpay_enabled
                && !paymentMethods.cod_enabled && (
                  <p
                    className="rounded-lg border border-[#7F1D1D] bg-red-50 px-4 py-3 text-sm text-[#7F1D1D]"
                    data-testid="payment-methods-unavailable"
                  >
                    No payment method is currently available. Please try again later.
                  </p>
                )}
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <div className="min-w-0 bg-white border border-[#E2DCD2] rounded-xl p-6 sticky top-32">
            <h2 className="font-serif text-2xl mb-4">Order Summary</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {items.map((i) => (
                <div key={i._k} className="flex items-center gap-3 text-sm">
                  <img src={i.image} alt="" className="w-12 h-12 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-1 line-clamp-1">
                      <FoodSymbol
                        value={i.food_symbol}
                        className="mt-0.5"
                        testId={`checkout-food-symbol-${i._k}`}
                      />
                      <span className="truncate">{i.name}</span>
                    </div>
                    <div className="text-xs text-[#64748B]">{i.variant_label} × {i.quantity}</div>
                    <div className="mt-2 inline-flex items-center rounded border border-[#E2DCD2]">
                      <button type="button" onClick={() => setQty(i._k, i.quantity - 1)} className="p-1.5" data-testid={`cart-decrease-${i._k}`}><Minus size={13} /></button>
                      <input type="number" min="1" max={cartStockLimit(i, items)} step="1" aria-label={`Quantity for ${i.name}`} value={i.quantity} onChange={(event) => { if (event.target.value !== "") setQty(i._k, Number(event.target.value)); }} className="w-16 bg-transparent text-center text-sm" data-testid={`cart-quantity-${i._k}`} />
                      <button type="button" disabled={i.quantity >= cartStockLimit(i, items)} onClick={() => setQty(i._k, i.quantity + 1)} className="p-1.5 disabled:opacity-40" data-testid={`cart-increase-${i._k}`}><Plus size={13} /></button>
                    </div>
                  </div>
                  <div className="font-medium">{payableInr(i.price * i.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2 min-w-0">
              <input
                placeholder="Coupon code"
                value={coupon}
                onChange={(event) => {
                  const nextCouponCode = event.target.value;
                  const noLongerMatchesApplied = appliedCoupon && (
                    appliedCoupon.code?.toUpperCase() !== nextCouponCode.trim().toUpperCase()
                  );
                  if (noLongerMatchesApplied) {
                    setAppliedCoupon(null);
                    setDiscount(0);
                    setCouponBenefit(null);
                  }
                  setCoupon(nextCouponCode);
                  setAutoAppliedCouponCode("");
                  setIsCouponInputManual(true);
                }}
                className="flex-1 min-w-0 bg-white border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm outline-none"
                data-testid="coupon-input"
              />
              <button
                type="button"
                onClick={() => {
                  if (couponFieldMatchesApplied) {
                    removeCoupon();
                    return;
                  }
                  applyCoupon();
                }}
                className={`px-4 rounded-lg text-sm text-white ${
                  couponFieldMatchesApplied ? "bg-emerald-600" : "bg-[#1E2A24]"
                }`}
                data-testid="apply-coupon"
              >
                {couponFieldMatchesApplied ? "Applied" : "Apply"}
              </button>
            </div>
            {autoAppliedCouponCode && appliedCoupon?.code === autoAppliedCouponCode && (
              <p
                className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#2D5A47]"
                data-testid="auto-applied-offer-note"
              >
                <Sparkles size={14} />
                Eligible offer applied automatically
              </p>
            )}
            {availableCoupons.length > 0 && (
              <div className="mt-4" data-testid="available-coupons">
                <button
                  type="button"
                  onClick={() => setOffersOpen((open) => !open)}
                  className={`w-full border px-4 py-3 text-left transition-colors ${
                    offersOpen
                      ? "border-[#2D5A47] bg-[#EBF2EE]"
                      : "border-[#C8BFB0] bg-[#FDFBF7] hover:border-[#2D5A47]"
                  }`}
                  data-testid="toggle-available-offers"
                  aria-expanded={offersOpen}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-[#2D5A47] text-white">
                        <Sparkles size={17} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-[#1E2A24]">
                          Available offers
                        </span>
                        <span className="mt-0.5 block text-xs text-[#64748B]">
                          Unlock a benefit for this order
                        </span>
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span
                        className="bg-[#D99B26] px-2 py-1 text-xs font-bold text-white"
                        data-testid="available-offers-count"
                      >
                        {availableCoupons.length} {availableCoupons.length === 1 ? "offer" : "offers"}
                      </span>
                      <ChevronDown
                        size={18}
                        className={`text-[#2D5A47] transition-transform ${
                          offersOpen ? "rotate-180" : "rotate-0"
                        }`}
                        data-testid="available-offers-chevron"
                      />
                    </span>
                  </span>
                </button>
                {offersOpen && (
                  <div className="border border-t-0 border-[#2D5A47] bg-white p-3" data-testid="available-offers-list">
                    <div className="space-y-2">
                      {availableCoupons.map((item) => {
                        const offerMatchesCurrentField = couponFieldMatchesApplied
                          && appliedCoupon?.code === item.code;
                        return (
                          <div
                            key={item.id}
                            className="flex items-center justify-between gap-3 border border-[#E2DCD2] bg-[#FDFBF7] px-3 py-3"
                            data-testid={`available-offer-summary-${item.code}`}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <Ticket size={16} className="shrink-0 text-[#C86D42]" />
                              <span className="min-w-0">
                                <span className="block font-mono text-sm font-bold text-[#2D5A47]">
                                  {item.code}
                                </span>
                                <span className="block truncate text-xs text-[#64748B]">
                                  {availableOfferLabel(item)}
                                </span>
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                if (offerMatchesCurrentField) {
                                  removeCoupon(false);
                                  return;
                                }
                                applyCouponCode(item.code, true);
                              }}
                              className={`shrink-0 px-3 py-1.5 text-xs font-semibold text-white ${
                                offerMatchesCurrentField ? "bg-emerald-600" : "bg-[#2D5A47]"
                              }`}
                              data-testid={`available-coupon-${item.code}`}
                            >
                              {offerMatchesCurrentField ? "Applied" : "Apply"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="mt-4 border-t border-[#E2DCD2] pt-4" data-testid="checkout-eira-points">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">Use Eira Points</div>
                  <p className="mt-1 text-xs text-[#64748B]" data-testid="checkout-eira-points-balance">
                    {points.available_points} available · 1 point = ₹1
                  </p>
                  <p className="mt-1 text-xs font-semibold text-[#2D5A47]" data-testid="checkout-tier-benefit">
                    {currentTier?.name || "Member"}: {tierRedemptionPercent}% redemption cap
                    {tierDiscountPercent > 0 ? ` · ${tierDiscountPercent}% tier discount` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleAllPoints}
                  disabled={maxPointsToRedeem <= 0}
                  className="group inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="checkout-use-all-points"
                  role="switch"
                  aria-checked={pointsToggleOn}
                >
                  <span className="text-right">
                    <span className="block text-xs font-semibold text-[#1E2A24]">
                      Use Eira Points
                    </span>
                    <span
                      className="block text-[10px] text-[#64748B]"
                      data-testid="checkout-points-toggle-status"
                    >
                      {usingAllAvailablePoints
                        ? `${payableInr(pointsDiscount)} all points applied`
                        : pointsToggleOn
                          ? `${payableInr(pointsDiscount)} custom amount applied`
                          : normalizedPointsToRedeem > 0
                            ? `${payableInr(normalizedPointsToRedeem)} selected`
                          : "Turn on to apply points"}
                    </span>
                  </span>
                  <span
                    className={`relative h-6 w-11 rounded-full transition-colors ${
                      pointsToggleOn ? "bg-[#2D5A47]" : "bg-[#C8BFB0]"
                    }`}
                    data-testid="checkout-points-toggle-track"
                  >
                    <span
                      className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                        pointsToggleOn ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </span>
                </button>
              </div>
              <input
                type="number"
                min="0"
                max={maxPointsToRedeem}
                step="0.01"
                value={pointsBlockedByCouponThreshold ? "" : pointsToRedeem}
                onChange={(event) => setCustomPoints(event.target.value)}
                disabled={pointsBlockedByCouponThreshold}
                className={`mt-3 w-full border px-3 py-2 text-sm outline-none ${
                  pointsToggleOn
                    ? "border-[#2D5A47] bg-[#F2F7F3]"
                    : "border-[#E2DCD2] focus:border-[#2D5A47]"
                }`}
                placeholder="Enter a custom points amount"
                aria-label="Custom Eira Points amount"
                data-testid="checkout-points-input"
              />
              {!pointsBlockedByCouponThreshold && (
                <p className="mt-2 text-xs text-[#64748B]" data-testid="checkout-points-custom-help">
                  Type an amount, then switch on to apply it. Clear the field, then switch on to use all points.
                </p>
              )}
              {pointsInputError && (
                <p className="mt-2 text-xs font-semibold text-red-700" data-testid="checkout-points-error">
                  {pointsInputError}
                </p>
              )}
              {pointsBlockedByCouponThreshold && (
                <p className="mt-2 text-xs text-[#8C6D46]" data-testid="coupon-points-stacking-note">
                  Eira Points unlock with BOGO or Free Item once the amount after the offer is above ₹999.
                </p>
              )}
              {points.pending_points > 0 && (
                <div className="mt-3" data-testid="checkout-eira-points-pending">
                  <p className="mb-2 text-xs text-[#8C6D46]">
                    {points.pending_points} points are pending and cannot be used yet.
                  </p>
                  <EiraPointsCountdown
                    nextAvailableAt={points.next_available_at}
                    nextAvailablePoints={points.next_available_points}
                    pendingPoints={points.pending_points}
                  />
                </div>
              )}
            </div>
            <div className="mt-4 space-y-2 text-sm border-t border-[#E2DCD2] pt-4">
              <div className="flex justify-between">
                <span>Product amount (inclusive GST)</span>
                <span>{payableInr(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-emerald-700" data-testid="checkout-coupon-discount">
                  <span>{couponBenefit?.kind === "bogo" ? "BOGO saving" : "Discount"}</span>
                  <span>−{payableInr(discount)}</span>
                </div>
              )}
              {couponBenefit?.free_item && (
                <div className="flex justify-between text-emerald-700" data-testid="checkout-free-item-benefit">
                  <span>{couponBenefitLabel(appliedCoupon, couponBenefit)}</span>
                  <span>Included</span>
                </div>
              )}
              {couponBenefit?.free_shipping && (
                <div className="flex justify-between text-emerald-700" data-testid="checkout-free-shipping-benefit">
                  <span>Free delivery coupon</span>
                  <span>Applied</span>
                </div>
              )}
              {pointsDiscount > 0 && (
                <div
                  className="flex justify-between text-[#2D5A47]"
                  data-testid="checkout-points-discount"
                >
                  <span>Eira Points</span>
                  <span>−{payableInr(pointsDiscount)}</span>
                </div>
              )}
              {tierDiscount > 0 && (
                <div
                  className="flex justify-between text-[#2D5A47]"
                  data-testid="checkout-tier-discount"
                >
                  <span>{currentTier?.name || "Tier"} discount</span>
                  <span>−{payableInr(tierDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Base Amount</span>
                <span>{payableInr(baseAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span>GST (18%, included)</span>
                <span>{payableInr(gst)}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>{shipping ? payableInr(shipping) : "Free"}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t border-[#E2DCD2] pt-2 mt-2">
                <span>Total</span>
                <span className="text-[#2D5A47]" data-testid="checkout-payable-total">
                  {payableInr(total)}
                </span>
              </div>
            </div>
            <button onClick={placeOrder} disabled={loading || !selectedPaymentAvailable} className="w-full mt-5 bg-[#C86D42] hover:bg-[#AE5931] text-white font-semibold py-3.5 rounded-lg disabled:opacity-50" data-testid="place-order">
              {loading ? (payMethod === "razorpay" ? "Opening secure payment…" : "Placing order…") : "Place Order"}
            </button>
          </div>
        </div>
      </div>
      <RecentlyViewedProducts className="mt-16 border-t border-[#E2DCD2] pt-10" />
    </div>
  );
};

export default Checkout;
